/**
 * Builder visual para condições compostas (AND/OR)
 * 
 * Permite combinar múltiplas condições em uma regra de roteamento
 * usando operadores lógicos AND ou OR.
 * 
 * Exemplos:
 * - Palavra-chave "vendas" E horário comercial
 * - Tag "VIP" OU primeira mensagem
 * 
 * @version 1.0.0
 * @date 2025-12-15
 */

import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ConditionFields } from './ConditionFields';
import { RoutingConditionType, conditionTypeLabels } from '@/pages/tenant/RoutingRules';

// ============= INTERFACES =============

/** Condição individual no formato composto */
export interface CompositeConditionItem {
  id: string;
  type: RoutingConditionType;
  value: Record<string, unknown>;
}

/** Configuração completa de condições compostas */
export interface CompositeConditionConfig {
  logic_operator: 'and' | 'or';
  conditions: CompositeConditionItem[];
}

interface CompositeConditionBuilderProps {
  /** Configuração atual das condições */
  config: CompositeConditionConfig;
  /** Callback quando configuração muda */
  onChange: (config: CompositeConditionConfig) => void;
}

// Cores para badges de condição (usando tokens do design system)
// v2.3: Adicionado webhook para sistemas externos
const conditionColors: Record<RoutingConditionType, string> = {
  keyword: 'bg-primary/10 text-primary border-primary/20',
  regex: 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20',
  first_message: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
  contact_tag: 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20',
  time_range: 'bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border-cyan-500/20',
  manual_execution: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
  webhook: 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20',
};

/**
 * Builder visual para condições compostas AND/OR
 */
export function CompositeConditionBuilder({
  config,
  onChange,
}: CompositeConditionBuilderProps) {
  // Adicionar nova condição
  const addCondition = () => {
    const newCondition: CompositeConditionItem = {
      id: crypto.randomUUID(),
      type: 'keyword',
      value: {},
    };

    onChange({
      ...config,
      conditions: [...config.conditions, newCondition],
    });
  };

  // Remover condição (mínimo 1)
  const removeCondition = (id: string) => {
    if (config.conditions.length <= 1) return;

    onChange({
      ...config,
      conditions: config.conditions.filter(c => c.id !== id),
    });
  };

  // Atualizar tipo de condição
  const updateConditionType = (id: string, type: RoutingConditionType) => {
    onChange({
      ...config,
      conditions: config.conditions.map(c =>
        c.id === id ? { ...c, type, value: {} } : c
      ),
    });
  };

  // Atualizar valor de condição
  const updateConditionValue = (id: string, value: Record<string, unknown>) => {
    onChange({
      ...config,
      conditions: config.conditions.map(c =>
        c.id === id ? { ...c, value } : c
      ),
    });
  };

  // Alterar operador lógico
  const toggleOperator = () => {
    onChange({
      ...config,
      logic_operator: config.logic_operator === 'and' ? 'or' : 'and',
    });
  };

  return (
    <div className="space-y-4">
      {/* Lista de condições */}
      {config.conditions.map((condition, index) => (
        <div key={condition.id}>
          {/* Operador lógico entre condições (não mostra antes da primeira) */}
          {index > 0 && (
            <div className="flex items-center justify-center my-3">
              <div className="flex-1 h-px bg-border" />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={toggleOperator}
                className="mx-3 px-4 font-semibold"
              >
                {config.logic_operator === 'and' ? 'E' : 'OU'}
              </Button>
              <div className="flex-1 h-px bg-border" />
            </div>
          )}

          {/* Card da condição */}
          <Card className="relative border-2 border-dashed hover:border-solid transition-all">
            <CardContent className="pt-4">
              {/* Header com tipo e botão remover */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Badge 
                    variant="outline" 
                    className={`${conditionColors[condition.type]} border`}
                  >
                    Condição {index + 1}
                  </Badge>
                </div>
                
                {/* Botão remover (desabilitado se for a única) */}
                {config.conditions.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeCondition(condition.id)}
                    className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>

              {/* Seletor de tipo + campos específicos */}
              <div className="space-y-4">
                {/* Seletor de tipo */}
                <div className="space-y-2">
                  <Label>Tipo de Condição</Label>
                  <Select
                    value={condition.type}
                    onValueChange={(value) => updateConditionType(condition.id, value as RoutingConditionType)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(conditionTypeLabels).map(([key, label]) => (
                        <SelectItem key={key} value={key}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Campos específicos do tipo */}
                <ConditionFields
                  conditionType={condition.type}
                  conditionValue={condition.value}
                  onTypeChange={(type) => updateConditionType(condition.id, type)}
                  onValueChange={(value) => updateConditionValue(condition.id, value)}
                  showTypeSelector={false}
                />
              </div>
            </CardContent>
          </Card>
        </div>
      ))}

      {/* Botão adicionar condição */}
      <Button
        type="button"
        variant="outline"
        onClick={addCondition}
        className="w-full border-dashed"
      >
        <Plus className="h-4 w-4 mr-2" />
        Adicionar Condição
      </Button>

      {/* Resumo das condições */}
      {config.conditions.length > 1 && (
        <div className="p-3 bg-muted/50 rounded-lg">
          <p className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">Resumo:</span>{' '}
            A regra será ativada quando{' '}
            <span className="font-medium text-primary">
              {config.logic_operator === 'and' 
                ? 'TODAS' 
                : 'QUALQUER UMA'
              }
            </span>{' '}
            das {config.conditions.length} condições forem satisfeitas.
          </p>
        </div>
      )}
    </div>
  );
}

// ============= FUNÇÕES AUXILIARES =============

/**
 * Converte formato legado (single condition) para formato composto
 */
export function legacyToComposite(
  conditionType: RoutingConditionType,
  conditionValue: Record<string, unknown>
): CompositeConditionConfig {
  return {
    logic_operator: 'and',
    conditions: [{
      id: crypto.randomUUID(),
      type: conditionType,
      value: conditionValue,
    }],
  };
}

/**
 * Verifica se o config é formato composto
 */
export function isCompositeFormat(value: Record<string, unknown>): boolean {
  return (
    'logic_operator' in value && 
    'conditions' in value && 
    Array.isArray(value.conditions)
  );
}

/**
 * Cria config composto vazio (para novas regras)
 */
export function createEmptyCompositeConfig(): CompositeConditionConfig {
  return {
    logic_operator: 'and',
    conditions: [{
      id: crypto.randomUUID(),
      type: 'keyword',
      value: {},
    }],
  };
}

/**
 * Gera descrição textual das condições compostas
 */
export function getCompositeConditionDescription(config: CompositeConditionConfig): string {
  const descriptions = config.conditions.map((cond, idx) => {
    const typeLabel = conditionTypeLabels[cond.type] || cond.type;
    let valueDesc = '';

    switch (cond.type) {
      case 'keyword':
        const keywords = (cond.value.keywords as string[]) || [];
        valueDesc = keywords.length > 0 ? keywords.slice(0, 2).join(', ') : '';
        if (keywords.length > 2) valueDesc += ` (+${keywords.length - 2})`;
        break;
      case 'regex':
        valueDesc = (cond.value.pattern as string) || '';
        if (valueDesc.length > 15) valueDesc = valueDesc.substring(0, 15) + '...';
        break;
      case 'time_range':
        valueDesc = `${cond.value.start || '??'}–${cond.value.end || '??'}`;
        break;
      case 'contact_tag':
        const tags = (cond.value.tag_ids as string[]) || [];
        valueDesc = `${tags.length} tag(s)`;
        break;
      case 'first_message':
        valueDesc = 'Nova conversa';
        break;
      case 'manual_execution':
        // Execução manual pelo agente - ações rápidas no chat
        valueDesc = 'Ação manual';
        break;
      case 'webhook':
        // Webhook externo - sistemas externos como ClickSign, ERPs
        valueDesc = 'Sistema externo';
        break;
    }

    return valueDesc ? `${typeLabel}: ${valueDesc}` : typeLabel;
  });

  const operator = config.logic_operator === 'and' ? ' E ' : ' OU ';
  return descriptions.join(operator);
}
